import { useCallback, useState } from 'react';
import { notification } from 'antd';
import { useField, useFormikContext } from 'formik';
import Button from 'components/new-ui/base/button/Button';
import useCustomers from 'hooks/useCustomers';
import { Customer as CustomerType } from 'store/customers/customer';
import useDebounce from 'utils/useDebounce';
import CustomerIconOutlined from 'components/icons/CustomerIconOutlined';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import useJobs from 'hooks/useJobs';
import { getCustomerFieldText } from 'views/shipments/create-shipment/utils';
import { NewShipmentFormFields, Steps } from '../../../types';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import CustomerAutocomplete from 'components/new-ui/composed/selects/CustomerAutocomplete';
import CreateNewCustomerDrawer from 'views/customers/components/create-customer-drawer/CreateNewCustomerDrawer';

const Customer = () => {
    const fieldName = 'customer.customerId';

    const { currentStepConfig, isIdle, isComplete, isActive, resetStep } =
        useMultiStepFormContext<NewShipmentFormFields>();
    const { editJob } = useJobs();
    const { customers, listCustomers } = useCustomers();
    const [field, meta, helpers] = useField(fieldName);
    const { values, validateForm } = useFormikContext<NewShipmentFormFields>();
    const [showCreateCustomerDrawer, setShowCreateCustomerDrawer] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerType | null>(null);
    const disabled = currentStepConfig?.disabled ?? false;

    const jobId = values.shipment.shipmentId;
    const stepKey = Steps.CUSTOMER;

    const querySearchCustomers = useCallback(
        async (query: string) => {
            if (query) {
                const params = { query };
                await listCustomers(params);
            } else {
                await listCustomers({ size: 200 });
            }
        },
        [listCustomers]
    );

    const debouncedSearchCustomers = useDebounce(querySearchCustomers, 500);

    const selectAndUpdateCustomer = async (customer: CustomerType) => {
        try {
            await editJob(jobId, { customerId: customer.id });
            setSelectedCustomer(customer);
            helpers.setValue(customer.id);
            validateForm();
        } catch (err) {
            notification.error({
                message: 'Could not update job with this customer',
            });
            setSelectedCustomer(null);
        }
    };

    const handleSelect = async (id: string) => {
        helpers.setTouched(true);

        const customer = customers.list.find((c) => c.id === id);

        if (!customer) {
            return;
        }

        await selectAndUpdateCustomer(customer);
    };

    const handleSearch = (val: string) => {
        if (selectedCustomer) {
            return;
        }

        debouncedSearchCustomers(val);
    };

    const handleOnCreateCustomer = async (customer: CustomerType) => {
        if (!customer.id) {
            console.log('Could not create customer');
            return;
        }

        selectAndUpdateCustomer(customer);
        setShowCreateCustomerDrawer(false);
    };

    const handleReset = () => {
        resetStep(stepKey, 'customerId');
        setSelectedCustomer(null);
    };

    const readonlyValue = selectedCustomer ? getCustomerFieldText(selectedCustomer) : '';
    const selectValue = selectedCustomer ? getCustomerFieldText(selectedCustomer) : undefined;

    if (isIdle(stepKey)) {
        return <FormSectionHeader title="Customer" icon={<CustomerIconOutlined />} />;
    }

    if (isComplete(stepKey)) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <FormSectionHeader title="Customer" icon={<CustomerIconOutlined />} />
                <ReadonlyFormItem disabled={disabled} onReset={handleReset} value={readonlyValue} />
            </div>
        );
    }

    if (isActive(stepKey)) {
        return (
            <div>
                <FormSectionHeader
                    title="Customer"
                    icon={<CustomerIconOutlined />}
                    subtitle="You can create a new customer or search for an existing one"
                    button={
                        <Button disabled={disabled} onClick={() => setShowCreateCustomerDrawer(true)}>
                            Create Customer
                        </Button>
                    }
                />
                <CustomerAutocomplete
                    inputStyle={{ width: '100%' }}
                    customers={customers.list}
                    disabled={disabled}
                    handleSelectCustomer={handleSelect}
                    search={handleSearch}
                    setCustomer={setSelectedCustomer}
                    onBlur={() => helpers.setTouched(true)}
                    fieldMeta={meta}
                    value={selectValue}
                />

                <CreateNewCustomerDrawer
                    destroyOnClose
                    open={showCreateCustomerDrawer}
                    onClose={() => setShowCreateCustomerDrawer(false)}
                    refreshCustomers={listCustomers}
                    handleOk={handleOnCreateCustomer}
                />
            </div>
        );
    }

    return null;
};

export default Customer;
